/* Browser error reporting (PRD I2): uncaught errors and rejected promises go to
   report_client_error (anon, rate-capped on the server) so the back office sees what breaks for
   real people. Pure helpers for the record and the per-session throttle; the installer is thin. */
import { supabase, supabaseConfigured } from "@/data/supabase";

export interface ErrorRecord { msg: string; stack: string; url: string }
const MAX_PER_SESSION = 5;
const IGNORE = [/ResizeObserver loop/, /Script error\.?$/, /Load failed$/, /The play\(\) request was interrupted/, /AbortError/, /NotAllowedError/, /Failed to fetch dynamically imported module/];

export function errorRecord(input: unknown, url = typeof location !== "undefined" ? location.href : ""): ErrorRecord | null {
  const e = (input && typeof input === "object" && "reason" in (input as object)) ? (input as { reason: unknown }).reason : (input && typeof input === "object" && "error" in (input as object) && (input as { error: unknown }).error) ? (input as { error: unknown }).error : input;
  const msg = e instanceof Error ? (e.name && e.name !== "Error" ? e.name + ": " : "") + e.message : typeof e === "string" ? e : (input && typeof input === "object" && "message" in (input as object)) ? String((input as { message: unknown }).message) : "";
  const m = String(msg || "").trim(); if (!m) return null;
  if (IGNORE.some((re) => re.test(m))) return null;
  const stack = e instanceof Error && e.stack ? String(e.stack).split("\n").slice(0, 8).join("\n") : "";
  return { msg: m.slice(0, 500), stack: stack.slice(0, 2000), url: redactUrl(url) };
}
/* Keep only the path: every capability travels in the hash (#reveal, #edit, #remote, #for, #verify) and the query strings
   carry gift codes, invite ids and legacy reveal tokens — none of it belongs in an ops table (2026-10-10 audit). */
export function redactUrl(url: string): string {
  const s = String(url || "");
  const path = s.replace(/^[a-z]+:\/\/[^/]+/i, "");
  const q = path.indexOf("?"), h = path.indexOf("#");
  const cut = Math.min(q < 0 ? Infinity : q, h < 0 ? Infinity : h);
  const base = cut === Infinity ? path : path.slice(0, cut);
  return (base + (q >= 0 ? "?…" : "") + (h >= 0 ? "#…" : "")).slice(0, 300);
}
/* One report per distinct message per session, a handful per session in all. Pure given the set. */
export function shouldReport(msg: string, seen: Set<string>, max = MAX_PER_SESSION): boolean {
  if (seen.has(msg) || seen.size >= max) return false; seen.add(msg); return true;
}
function viewerId(): string { try { const k = "ksp_eid"; let v = localStorage.getItem(k); if (!v) { v = Math.random().toString(36).slice(2, 12); localStorage.setItem(k, v); } return v; } catch { return ""; } }

let installed = false;
export function installErrorReporting() {
  if (installed || typeof window === "undefined" || !supabaseConfigured || import.meta.env.DEV) return;
  installed = true;
  const seen = new Set<string>();
  const send = (input: unknown) => {
    try {
      const rec = errorRecord(input); if (!rec || !shouldReport(rec.msg, seen)) return;
      supabase().rpc("report_client_error", { p_msg: rec.msg, p_stack: rec.stack, p_url: rec.url, p_ua: navigator.userAgent.slice(0, 200), p_viewer: viewerId() }).then(() => {}, () => {});
    } catch { /* never let the reporter throw */ }
  };
  window.addEventListener("error", (ev) => send(ev));
  window.addEventListener("unhandledrejection", (ev) => send(ev));
}
